import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Facebook, Instagram, Music2 } from "lucide-react";
import { fetchSiteSettings } from "@/lib/firebase";
import GoogleReviewButton from "./GoogleReviewButton";
import GoogleMerchantWidget from "./GoogleMerchantWidget";

const socials = [
  {
    label: "Facebook",
    href: "https://web.facebook.com/cvsix.collection",
    Icon: Facebook,
  },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@trendyquesty",
    Icon: Music2,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/trend_yquest/",
    Icon: Instagram,
  },
];

export default function Footer() {
  const [siteSettings, setSiteSettings] = useState({ brandName: "Trendy Quest", logoUrl: "" });

  useEffect(() => {
    fetchSiteSettings().then(setSiteSettings).catch(() => {});
  }, []);

  return (
    <footer className="border-t border-[#e7dcc0] bg-[#fbfaf5]">
      <div className="grid w-full gap-10 px-4 py-14 md:grid-cols-[1.2fr_0.8fr_0.9fr] md:px-6">
        <div>
          <div className="flex items-center gap-3">
            {siteSettings.logoUrl ? (
              <div className="relative h-12 w-12 overflow-hidden rounded-full bg-white p-1 ring-2 ring-[#e7dcae]">
                <Image
                  src={siteSettings.logoUrl}
                  alt={siteSettings.brandName}
                  fill
                  sizes="48px"
                  className="object-contain"
                />
              </div>
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green text-lg font-bold text-white">
                TQ
              </div>
            )}
            <div>
              <p className="font-playfair text-2xl font-bold text-green">{siteSettings.brandName}</p>
              <p className="text-sm text-gray-600">Quality footwear for confident everyday living.</p>
            </div>
          </div>

          <p className="mt-5 max-w-md text-sm leading-7 text-gray-600">
            At Trendy Quest Collection, we bring you quality footwear that blends style, comfort, and
            everyday practicality. Our mission is simple - help you step out confidently, every single day.
          </p>
        </div>

        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Quick Links</p>
          <div className="mt-5 flex flex-col gap-3 text-sm text-gray-700">
            <Link href="/" className="transition-colors hover:text-green">Home</Link>
            <Link href="/products" className="transition-colors hover:text-green">Shop</Link>
            <Link href="/about" className="transition-colors hover:text-green">About Us</Link>
            <Link href="/faq" className="transition-colors hover:text-green">FAQ</Link>
            <Link href="/privacy-policy" className="transition-colors hover:text-green">Privacy Policy</Link>
            <Link href="/contact" className="transition-colors hover:text-green">Contact</Link>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-green">Follow Us</p>
          <div className="mt-5 flex flex-col gap-3 text-sm text-gray-700">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-full border border-transparent px-3 py-2 transition hover:border-[#e7dcc0] hover:bg-white hover:text-green"
              >
                <social.Icon size={18} strokeWidth={1.9} />
                {social.label}
              </a>
            ))}
          </div>

          <div className="mt-6 rounded-2xl border border-[#ebe3ce] bg-white p-6 text-sm leading-7 text-gray-600 shadow-sm">
            <p className="font-semibold text-gray-900">Contact & Location</p>
            <div className="mt-3 space-y-2">
              <a href="tel:+254717002340" className="block text-gray-700 transition hover:text-green">Phone / WhatsApp: +254 717 002 340</a>
              <a href="mailto:pattohkarimi@gmail.com" className="block text-gray-700 transition hover:text-green">Email: pattohkarimi@gmail.com</a>
              <p>Store: Dubai Merchant Mall, Ronald Ngala Street, Shop G18, Nairobi</p>
              <p className="text-sm text-gray-600">Open our location in Google Maps for directions and store details.</p>
            </div>
            <GoogleReviewButton />
          </div>
        </div>
      </div>
      {/* Google Merchant Widget - displays seller ratings badge */}
      <GoogleMerchantWidget />
    </footer>
  );
}
